import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { Banner, Empty, ErrorNote, Spinner, Tag } from '@/components/ui'
import { useChannel } from '@/lib/channel'

type Rule = {
  reminder_id: string; ref: string; active: boolean; label: string; at: string
  priorities: string[]; audience: string; condition: string; describes: string
  valid: boolean; problems: string[]; row_version: number
}
type Choice = { value: string; label: string; hint: string }
type Options = { priorities: string[]; audiences: Choice[]; conditions: Choice[] }
type Preview = {
  matched_tasks: number; messages: number
  recipients: { user_id: string; name: string; tasks: number; refs: number[] }[]
  unreachable: { user_id: string; name: string; tasks: number; why: string }[]
  orphaned_tasks: number; problems: string[]; describes: string
}
type Draft = {
  label: string; at: string; priorities: string[]
  audience: string; condition: string; active: boolean
}

// Working days in a typical month. Only used to turn "3 messages" into a monthly
// figure, which is the number that actually decides whether a reminder is worth it.
const WORKING_DAYS_A_MONTH = 25

const BLANK: Draft = {
  label: '', at: '14:00', priorities: [],
  audience: 'RESPONSIBLE', condition: 'OPEN', active: true,
}

/** The rules worth having, offered as one tap.
 *
 *  Every one of these was already expressible in this form, and the Owner asked for
 *  a feature that existed - which is the signal that the form states the mechanism
 *  and not the intention. A preset is the cheapest cure: it says what the rule is
 *  *for* in the Owner's words, then fills in the audience/condition pair that says
 *  it in the machine's.
 */
const PRESETS: { name: string; shape: string; draft: Draft }[] = [
  {
    name: 'Chase approvers',
    shape: 'The approver · waiting for approval · 16:00',
    draft: {
      label: 'Afternoon approval chase', at: '16:00', priorities: [],
      audience: 'APPROVER', condition: 'PENDING_APPROVAL', active: true,
    },
  },
  {
    name: 'Chase work that has slipped',
    shape: 'The person responsible · already carried forward · 11:00',
    draft: {
      label: 'Carried-forward chase', at: '11:00', priorities: [],
      audience: 'RESPONSIBLE', condition: 'OVERDUE', active: true,
    },
  },
  {
    name: "Ask me about today's list",
    shape: "The Owner · today's list · 08:00",
    draft: {
      label: "Today's list", at: '08:00', priorities: [],
      audience: 'OWNER', condition: 'DAY_NOT_WRITTEN', active: true,
    },
  },
]

export function Reminders() {
  const { onTelegram } = useChannel()
  const rules = useQuery({ queryKey: ['reminders'], queryFn: () => api.get<Rule[]>('/reminders') })
  const options = useQuery({ queryKey: ['reminder-options'], queryFn: () => api.get<Options>('/reminders/options') })
  const [editing, setEditing] = useState<Rule | 'new' | null>(null)

  if (rules.isLoading || options.isLoading) return <Spinner />
  if (rules.error) return <ErrorNote error={rules.error} />

  const rows = rules.data ?? []
  const active = rows.filter((r) => r.active)
  const broken = rows.filter((r) => !r.valid)

  return (
    <>
      {broken.length > 0 ? (
        <Banner tone="bad">
          {broken.length} reminder{broken.length === 1 ? '' : 's'} cannot be scheduled
          and will never fire. Open it to see why.
        </Banner>
      ) : null}

      <div className="card">
        <div className="card-head">
          <b className="text-[13px]">Reminders</b>
          <span className="flex-1" />
          <span className="text-[11.8px] text-ink-3">
            {active.length} active · about{' '}
            {active.length * WORKING_DAYS_A_MONTH} message
            {active.length * WORKING_DAYS_A_MONTH === 1 ? '' : 's'} a month per person
            reached
          </span>
          <button className="btn btn-sm btn-primary" onClick={() => setEditing('new')}>
            New reminder
          </button>
        </div>

        {rows.length === 0 ? (
          <Empty
            title="No reminders"
            hint="The morning digest still goes out at 09:30. Reminders are the extra nudges during the day — and each one can be narrowed to the priorities worth interrupting somebody for."
            action={<button className="btn btn-primary" onClick={() => setEditing('new')}>
              Create the first reminder</button>} />
        ) : (
          <div className="table-wrap">
            <table className="w-full border-collapse">
              <thead><tr>
                <th className="th w-14">Ref</th><th className="th w-16">Time</th>
                <th className="th">Name</th>
                <th className="th w-52">Priorities</th>
                <th className="th w-44">About</th>
                <th className="th w-40">Sent to</th>
                <th className="th w-24">Active</th>
                <th className="th w-16"></th>
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.reminder_id} className={r.active ? '' : 'opacity-55'}>
                    <td className="td mono text-xs">{r.ref}</td>
                    <td className="td mono text-[12.5px] font-semibold">{r.at}</td>
                    <td className="td">
                      <b className="font-semibold">{r.label}</b>
                      {r.problems.length > 0 ? (
                        <span className="block text-[11.5px] text-rej-ink mt-0.5">
                          {r.problems[0]}
                        </span>
                      ) : null}
                    </td>
                    <td className="td">
                      {r.priorities.length === 0
                        ? <span className="text-ink-3 text-[12.5px]">every priority</span>
                        : <span className="flex gap-1 flex-wrap">
                            {r.priorities.map((p) => <Tag key={p}>{p.toLowerCase()}</Tag>)}
                          </span>}
                    </td>
                    <td className="td text-[12.5px]">
                      {labelFor(options.data?.conditions, r.condition)}
                    </td>
                    <td className="td text-[12.5px]">
                      {labelFor(options.data?.audiences, r.audience)}
                    </td>
                    <td className="td text-[12.5px]">
                      {r.active ? 'Yes' : <span className="text-ink-3">Paused</span>}
                    </td>
                    <td className="td">
                      <button className="btn btn-sm" onClick={() => setEditing(r)}>Edit</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="px-4 py-2.5 text-[11.8px] text-ink-3 border-t border-rule">
          {/* The cost sentence is the whole reason to narrow a reminder by priority,
              and it is only true on WhatsApp. Telling a Telegram install to restrict
              reminders to control a charge that does not exist is worse than saying
              nothing - it argues for the wrong thing. */}
          {onTelegram
            ? <>Telegram messages cost nothing, so the limit on how many reminders a
                day is people&rsquo;s attention rather than the bill. </>
            : <>Every reminder is one billed WhatsApp message per person reached, so
                narrowing by priority is what keeps several a day affordable. </>}
          Reminders never fire on a non-working day, and each one sends at most once a
          day.
        </div>
      </div>

      {/* The built-in chase is invisible here otherwise, and it overlaps with any
          approver rule above - so an Owner who writes one gets two nudges and no
          explanation for the second. */}
      <div className="card mt-4 px-4 py-3">
        <b className="block text-[12.5px] font-semibold mb-1">
          There is also a built-in chase for approvals
        </b>
        <p className="text-[11.8px] text-ink-3">
          Separately from the rules above, an approver with anything waiting{' '}
          <b>a day or more</b> is chased once a day, some time between 09:00 and 19:00.
          It covers every pending approval rather than just today&rsquo;s list, it
          cannot be narrowed by priority, and it exists so that an install with no
          rules at all still chases stalled approvals.
        </p>
        <p className="text-[11.8px] text-ink-3 mt-1.5">
          Once you have set your own approver reminder above, turn it off — otherwise
          approvers get both. It is <span className="mono">approval_nudge_hourly</span>{' '}
          on the Settings tab of Master data.
        </p>
      </div>

      {editing ? (
        <EditReminder
          rule={editing === 'new' ? null : editing}
          options={options.data!}
          onClose={() => setEditing(null)} />
      ) : null}
    </>
  )
}

function labelFor(choices: Choice[] | undefined, value: string) {
  return choices?.find((c) => c.value === value)?.label ?? value
}

function EditReminder({ rule, options, onClose }: {
  rule: Rule | null; options: Options; onClose: () => void
}) {
  const qc = useQueryClient()
  const [err, setErr] = useState<unknown>(null)
  const [form, setForm] = useState<Draft>(rule ? {
    label: rule.label, at: rule.at, priorities: rule.priorities,
    audience: rule.audience, condition: rule.condition, active: rule.active,
  } : BLANK)
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  // Debounced so dragging through the priority checkboxes does not fire a request
  // per click. The preview runs the same code the scheduled send runs, so what it
  // shows is what would actually go out.
  const [settled, setSettled] = useState(form)
  useEffect(() => {
    const t = setTimeout(() => setSettled(form), 350)
    return () => clearTimeout(t)
  }, [form])

  // The zone the `at` field is expressed in. It said "(IST)" in the label, which was
  // a fact about the install and is now a setting - so a firm that moves gets a form
  // asking for a time in a zone it does not use.
  const tz = useQuery({
    queryKey: ['timezone'],
    queryFn: () => api.get<{ timezone: string }>('/timezone'),
  })
  const tzName = tz.data?.timezone ?? 'the firm\'s zone'

  const preview = useQuery({
    queryKey: ['reminder-preview', settled],
    queryFn: () => api.post<Preview>('/reminders/preview', settled),
    enabled: /^([01]\d|2[0-3]):[0-5]\d$/.test(settled.at),
    retry: false,
  })

  const save = useMutation({
    mutationFn: () => rule
      ? api.patch(`/reminders/${rule.reminder_id}?expected_version=${rule.row_version}`, form)
      : api.post('/reminders', form),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['reminders'] }); onClose() },
    onError: setErr,
  })
  const togglePriority = (p: string) =>
    set('priorities', form.priorities.includes(p)
      ? form.priorities.filter((x) => x !== p)
      : [...form.priorities, p])

  const timeOk = /^([01]\d|2[0-3]):[0-5]\d$/.test(form.at)

  return (
    <>
      <div className="fixed inset-0 bg-ink/10 z-10" onClick={onClose} />
      <aside className="fixed right-0 top-0 bottom-0 w-full sm:w-[560px] bg-surface border-l border-rule
                        shadow-2xl z-20 flex flex-col">
        <div className="px-5 py-3.5 border-b border-rule flex items-center">
          <h3 className="text-base font-semibold tracking-tight">
            {rule ? `Edit ${rule.ref}` : 'New reminder'}
          </h3>
          <span className="flex-1" />
          <button onClick={onClose} className="text-ink-3 hover:text-ink text-lg px-1">×</button>
        </div>

        <div className="flex-1 overflow-auto p-5 space-y-4">
          {err ? <ErrorNote error={err} /> : null}

          {/* Only on a new rule: on an existing one a preset would silently discard
              what the Owner already set. */}
          {!rule ? (
            <div className="rounded-md border border-rule bg-paper p-3">
              <div className="label mb-1.5">Start from one of these</div>
              <div className="space-y-1.5">
                {PRESETS.map((p) => (
                  <button key={p.name} type="button"
                          className="w-full text-left rounded border border-rule bg-surface
                                     px-2.5 py-1.5 hover:border-primary"
                          onClick={() => setForm(p.draft)}>
                    <b className="block text-[12.5px] font-semibold">{p.name}</b>
                    <span className="mono block text-[11px] text-ink-3">{p.shape}</span>
                  </button>
                ))}
              </div>
              <p className="text-[11.5px] text-ink-3 mt-2">
                Each one just fills in the form below — change anything before saving.
              </p>
            </div>
          ) : null}

          <div className="grid grid-cols-1 sm:grid-cols-[1fr_90px] gap-3">
            <div>
              <label className="label">Name</label>
              <input className="field" value={form.label} autoFocus
                     placeholder="Late morning urgent chase"
                     onChange={(e) => set('label', e.target.value)} />
            </div>
            <div>
              <label className="label" htmlFor="rule-at">Time ({tzName})</label>
              {/* A real time field, so the browser gives its own picker - a clock
                  dial on a phone, a stepper on a desktop. This was a text box with
                  an "enter it as HH:MM" hint underneath, which is a validation rule
                  standing in for a control.

                  The value a time input yields is always 24-hour `HH:MM` whatever
                  the browser shows the person, so what reaches the API is unchanged.
                  The regex guard below stays: a time input can still be empty. */}
              <input id="rule-at" className="field mono" type="time"
                     value={form.at}
                     onChange={(e) => set('at', e.target.value)} />
            </div>
          </div>

          <div>
            <label className="label">Only these priorities</label>
            <div className="flex gap-3 flex-wrap">
              {options.priorities.map((p) => (
                <label key={p} className="flex items-center gap-1.5 text-[12.5px]">
                  <input type="checkbox" checked={form.priorities.includes(p)}
                         onChange={() => togglePriority(p)} />
                  {p}
                </label>
              ))}
            </div>
            <p className="text-[11.5px] text-ink-3 mt-1">
              None ticked means every priority. Narrowing to Urgent is what makes an
              extra reminder cheap — it reaches two or three people, not the firm.
            </p>
          </div>

          <div>
            <label className="label">About tasks that are</label>
            <div className="space-y-1.5">
              {options.conditions.map((cnd) => (
                <label key={cnd.value} className="flex gap-2 items-start text-[12.5px]">
                  <input type="radio" name="condition" className="mt-0.5"
                         checked={form.condition === cnd.value}
                         onChange={() => set('condition', cnd.value)} />
                  <span>
                    <b className="font-semibold">{cnd.label}</b>
                    <span className="block text-[11.5px] text-ink-3">{cnd.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="label">Send it to</label>
            <div className="space-y-1.5">
              {options.audiences.map((a) => (
                <label key={a.value} className="flex gap-2 items-start text-[12.5px]">
                  <input type="radio" name="audience" className="mt-0.5"
                         checked={form.audience === a.value}
                         onChange={() => set('audience', a.value)} />
                  <span>
                    <b className="font-semibold">{a.label}</b>
                    <span className="block text-[11.5px] text-ink-3">{a.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2 text-[13px]">
            <input type="checkbox" checked={form.active}
                   onChange={(e) => set('active', e.target.checked)} />
            Active
          </label>

          {/* The preview. Worth the room: it is the difference between guessing and
              knowing what a reminder costs and who it interrupts. */}
          <div className="border-t border-rule pt-4">
            <label className="label">If this ran now</label>
            {!timeOk ? (
              <p className="text-[12.5px] text-rej-ink">
                Pick a time above to see who this would reach.
              </p>
            ) : preview.isLoading ? <Spinner label="Working it out" />
              : preview.error ? <ErrorNote error={preview.error} />
              : preview.data ? <PreviewPanel p={preview.data} /> : null}
          </div>
        </div>

        <div className="px-5 py-3.5 border-t border-rule flex gap-2 items-center">
          <button className="btn btn-primary" disabled={!timeOk || !form.label.trim() || save.isPending}
                  onClick={() => { setErr(null); save.mutate() }}>
            {save.isPending ? 'Saving…' : rule ? 'Save' : 'Create'}
          </button>
          <button className="btn" onClick={onClose}>Cancel</button>
          <span className="flex-1" />
          {rule ? <DeleteButton rule={rule} onDone={onClose} /> : null}
        </div>
      </aside>
    </>
  )
}

function PreviewPanel({ p }: { p: Preview }) {
  return (
    <div className="space-y-2.5">
      {p.problems.map((problem) => (
        <div key={problem} className="text-[12px] bg-pend-tint text-pend-ink px-2.5 py-1.5 rounded">
          {problem}
        </div>
      ))}

      <div className="text-[12.5px]">
        <b className="font-semibold">{p.matched_tasks}</b> matching task
        {p.matched_tasks === 1 ? '' : 's'} · <b className="font-semibold">{p.messages}</b>{' '}
        message{p.messages === 1 ? '' : 's'}
        {p.messages > 0 ? (
          <span className="text-ink-3">
            {' '}— about {p.messages * WORKING_DAYS_A_MONTH} a month
          </span>
        ) : null}
      </div>

      {p.recipients.length > 0 ? (
        <div className="table-wrap">
          <table className="w-full border-collapse">
            <tbody>
              {p.recipients.map((r) => (
                <tr key={r.user_id}>
                  <td className="td">{r.name}</td>
                  <td className="td mono text-xs text-ink-3">
                    {r.refs.slice(0, 6).map((n) => '#' + n).join(' ')}
                    {r.refs.length > 6 ? ` +${r.refs.length - 6}` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-[12.5px] text-ink-3">
          Nobody would be messaged today. That may just mean there is no matching work
          right now.
        </p>
      )}

      {p.unreachable.length > 0 ? (
        <div className="text-[12px] bg-pend-tint text-pend-ink px-2.5 py-1.5 rounded">
          <b className="font-semibold">
            {p.unreachable.length} person{p.unreachable.length === 1 ? '' : 's'} matched
            but cannot be reached:
          </b>
          {p.unreachable.map((u) => (
            <span key={u.user_id} className="block">{u.name} — {u.why}</span>
          ))}
          <span className="block mt-1">Fix it on the People page.</span>
        </div>
      ) : null}

      {p.orphaned_tasks > 0 ? (
        <p className="text-[11.5px] text-ink-3">
          {p.orphaned_tasks} matching task{p.orphaned_tasks === 1 ? ' has' : 's have'} no
          such person set, so nobody is told about {p.orphaned_tasks === 1 ? 'it' : 'them'}.
        </p>
      ) : null}
    </div>
  )
}

function DeleteButton({ rule, onDone }: { rule: Rule; onDone: () => void }) {
  const qc = useQueryClient()
  const [confirming, setConfirming] = useState(false)
  const del = useMutation({
    mutationFn: () => api.del(`/reminders/${rule.reminder_id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['reminders'] }); onDone() },
  })
  // Two taps rather than a browser confirm(): a modal dialog inside the drawer would
  // be the third layer of overlay, and this is not a destructive-enough action for it.
  return confirming ? (
    <span className="flex gap-2 items-center text-[12.5px]">
      <span className="text-ink-3">Delete {rule.ref}?</span>
      <button className="btn btn-sm btn-bad" disabled={del.isPending}
              onClick={() => del.mutate()}>Yes, delete</button>
      <button className="btn btn-sm" onClick={() => setConfirming(false)}>No</button>
    </span>
  ) : (
    <button className="btn btn-sm" onClick={() => setConfirming(true)}>Delete</button>
  )
}
